import React, { useEffect, useState, useCallback } from 'react';
import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { ACCOUNT_FIELDS } from 'helpers/constantsHelper';
import { t, generateDefaultAccountFieldMap } from 'helpers/componentHelper';
import { getAccount, saveAccount, InvitesView, consumeInvite } from 'services/accountApi';
import { CognitoUserAmplify } from '@aws-amplify/ui';

import './MyAccount.scss';

function MyAccount({
  userGroup,
  loggedInUser,
}: {
  userGroup: string;
  loggedInUser: CognitoUserAmplify;
}) {
  const defaultFieldMap = generateDefaultAccountFieldMap(loggedInUser);
  const [fieldMap, setFieldMap] = useState(defaultFieldMap);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [currentUserInvites, setCurrentUserInvites] = useState<InvitesView | null>(null);

  // Pull the saved profile on mount. 404 from the API means "user hasn't
  // saved yet" — leave the default (Cognito-derived) field map in place.
  // Other errors surface in a non-blocking message slot below the form.
  useEffect(() => {
    let cancelled = false;
    getAccount()
      .then((account) => {
        if (cancelled || !account) return;
        setCurrentUserInvites(account.invites);
        setFieldMap((prev) => ({
          ...prev,
          firstname: account.account?.firstname ?? '',
          lastname: account.account?.lastname ?? '',
          email: account.account?.email ?? '',
          phone: account.account?.phone ?? '',
        }));
      })
      .catch(() => {
        // Swallow — the form stays usable with the default values.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmitHandler = () => {
    // validate fields are populated
    let valid = true;
    Object.keys(fieldMap).forEach((key) => {
      if (ACCOUNT_FIELDS[key]?.required && !fieldMap[key]) {
        valid = false;
      }
    });
    if (!valid) return;

    // Country is fixed at "USA" by the UI (the input is disabled). Send it
    // explicitly so the server doesn't have to default — keeps the round-trip
    // shape lossless for the user.
    saveAccount({
      firstname: fieldMap[ACCOUNT_FIELDS.firstname.id] ?? '',
      lastname: fieldMap[ACCOUNT_FIELDS.lastname.id] ?? '',
      email: fieldMap[ACCOUNT_FIELDS.email.id] ?? '',
      phone: fieldMap[ACCOUNT_FIELDS.phone.id] ?? '',
    }).catch(() => {
      // Best-effort write; server-side validation errors will already
      // be surfaced via the per-field errorText prop in the form below.
    });
  };

  const onEmailHandler = useCallback(() => {
    if (inviteEmail) {
      consumeInvite(inviteEmail).then(() => {
        setCurrentUserInvites((prev) =>
          prev
            ? {
                ...prev,
                remaining: prev.remaining > 0 ? prev.remaining - 1 : 0,
                used: prev.used + 1,
              }
            : null,
        );
        setInviteEmail(null);
      });
    }
  }, [inviteEmail]);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldMap({ ...fieldMap, [event.currentTarget?.id]: event.currentTarget?.value });
  };
  const showInvites = userGroup?.toLowerCase()?.includes('ad');
  return (
    <div className={'account'}>
      <div className={'account__header'}>
        <div>{`${t('pages.account.header')}`}</div>
        <div className={'account__header--subtext'}>{`${t('pages.account.headerSubtext')}`}</div>
      </div>
      <div className={'account__flex--row'}>
        <div>
          <label>{`${t('pages.account.fields.firstN')}`}</label>
          <div>
            <Input
              id={ACCOUNT_FIELDS.firstname.id}
              value={fieldMap[ACCOUNT_FIELDS.firstname.id]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
              errorText={
                !fieldMap[ACCOUNT_FIELDS.firstname.id] ? `${t('pages.account.required')}` : ''
              }
            />
          </div>
        </div>
        <div>
          <label>{`${t('pages.account.fields.lastN')}`}</label>
          <div>
            <Input
              id={ACCOUNT_FIELDS.lastname.id}
              value={fieldMap[ACCOUNT_FIELDS.lastname.id]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
              errorText={
                !fieldMap[ACCOUNT_FIELDS.lastname.id] ? `${t('pages.account.required')}` : ''
              }
            />
          </div>
        </div>
      </div>
      <div className={'account__flex--row'}>
        <div>
          <label>{`${t('pages.account.fields.email')}`}</label>
          <div>
            <Input
              id={ACCOUNT_FIELDS.email.id}
              value={fieldMap[ACCOUNT_FIELDS.email.id]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
              errorText={!fieldMap[ACCOUNT_FIELDS.email.id] ? `${t('pages.account.required')}` : ''}
            />
          </div>
        </div>
        <div>
          <label>{`${t('pages.account.fields.phone')}`}</label>
          <div>
            <Input
              id={ACCOUNT_FIELDS.phone.id}
              value={fieldMap[ACCOUNT_FIELDS.phone.id]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
              errorText={!fieldMap[ACCOUNT_FIELDS.phone.id] ? `${t('pages.account.required')}` : ''}
            />
          </div>
        </div>
      </div>
      <div className={'account__flex--row-center account--footer'}>
        <div className={'account__flex--row-center'}>
          <input
            id="agree"
            type={'checkbox'}
            checked={true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
          />
          <label className={'account__agree--label'}>{`${t('pages.account.fields.agree')}`}</label>
        </div>
        <div className={'account__button'}>
          <Button text={`${t('app.buttons.submit')}`} onClick={() => onSubmitHandler()} />
        </div>
      </div>
      {showInvites && (
        <div className={'account__invite-info'}>
          <div className={'account__invite-header'}>{`${t(
            'pages.account.invites.inviteHeader',
          )}`}</div>
          <p className={'account__invite-info--remaining-container'}>
            <span>{`${t('pages.account.invites.remaining')}`}</span>
            <span className={'account__invite-info--remaining'}>
              {currentUserInvites?.remaining}
            </span>
          </p>
          <div className={'account__invite-form'}>
            <div>
              <span>{`${t('pages.account.invites.inviteEmail')}`}</span>
              <span>
                <Input
                  id="invite"
                  value={inviteEmail ?? ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setInviteEmail(event.currentTarget.value as string);
                  }}
                />
              </span>
            </div>
            <Button
              text={`${t('pages.account.invites.sendInvite')}`}
              onClick={() => onEmailHandler()}
            />
          </div>
          <p>
            <span className={'account__invite-info--disclaimer'}>
              {`${t('pages.account.invites.disclaimer')}`}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

export default MyAccount;
